import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Checkbox as MUICheckbox,
  TextField,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useParams } from 'react-router-dom'
import DatePickerField from '~components/forms/date-picker-field/date-picker-field'
import { SelectField } from '~components/forms/select-field/select-field'
import { TextInputField } from '~components/forms/text-input-field/text-input-field'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { ModalFooter } from '~components/molecules/modal/modal.styles'
import { Checkbox } from '~components/web-components'
import { useExtractCertificateFields } from '~hooks/queries/documents/use-extract-certificate-fields'
import { useOcrDocumentText } from '~hooks/queries/documents/use-ocr-document-text'
import { usePostDocuments } from '~hooks/queries/documents/use-post-documents'
import { IDocumentsRowData } from '~hooks/queries/vessels/documents/types'
import { Z_INDEX } from '~types/z-index'
import { forceDateToZuluTimezone, isDateExpired } from '~utils/dates'
import { gtm } from '~utils/gtm'
import { invalidInputMessage } from '~utils/invalid-input-message'
import { DragAndDropFiles } from '../../questionnaire-page-v2/components/drag-and-drop-files'
import { RESTRICTED_CERTIFICATES_TYPE } from '../document-types'
import { DocumentTagsField } from './document-tags-field'

export const certificateIssuerOptions = [
  'class',
  'flag',
  'port State',
  'insurance',
  'other',
].map((certificateIssuer) => ({
  label: `${certificateIssuer.charAt(0).toUpperCase()}${certificateIssuer.slice(1)}`,
  value: certificateIssuer.replaceAll(' ', ''),
}))

export const certificateCategories = [
  'Registration & Ownership',
  'Classification',
  'Construction & Load Line',
  'SOLAS (Safety)',
  'ISM (Safety Management)',
  'ISPS (Security)',
  'MARPOL (Environmental)',
  'Crew & Labour',
  'Liability & Insurance',
  'Other (Operational)',
]

export interface IDocumentsByType {
  documents: IDocumentsRowData[]
  type: string
}

const getIsDateRequired = (type: string): boolean =>
  type === 'Trading Certificates' ||
  type === 'Certificates Impacting Port Expense'

export const ALLOWED_EXTENSIONS = new Set([
  'pdf',
  'txt',
  'doc',
  'docx',
  'jpeg',
  'jpg',
  'png',
  'heic',
  'heif',
  'xlsx',
  'xls',
  'ppt',
  'pptx',
  'bmp',
])

const AddDocumentModalContent = ({
  existingDocumentTags = [],
  documentsByType = [],
  setFilesToPost,
  filesToPost,
  showTechManagerDocuments,
  noExpiryDate,
  setNoExpiryDate,
  noNextSurvey,
  setNoNextSurvey,
}: {
  existingDocumentTags: string[]
  documentsByType: IDocumentsByType[]
  setFilesToPost: React.Dispatch<
    React.SetStateAction<{ key: string; file: File }[] | undefined>
  >
  filesToPost: { key: string; file: File }[] | undefined
  showTechManagerDocuments: boolean
  noExpiryDate: boolean
  setNoExpiryDate: React.Dispatch<React.SetStateAction<boolean>>
  noNextSurvey: boolean
  setNoNextSurvey: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])
  const ocrMutation = useOcrDocumentText()
  const extractMutation = useExtractCertificateFields()
  const { setStatus } = useAnimatedAlert()
  const { setValue, watch, register } = useFormContext()
  const isDigitalCertificate = watch('digitalCertificate')
  const hasFile = filesToPost && filesToPost.length > 0
  const hasTooManyFiles = !!filesToPost && filesToPost.length > 1
  const isOcrLoading = ocrMutation.isPending || extractMutation.isPending

  const uniqueDocumentTypeOptions = documentsByType
    .filter((documentType) => documentType.type !== 'Other')
    .map((documentType) => {
      return {
        label: documentType.type,
        value: documentType.type,
      }
    })
  const documentTypeOptions = showTechManagerDocuments
    ? [
        { label: 'Primary Certificates', value: 'Primary Certificates' },
        {
          label: 'Primary Certificates (Other)',
          value: 'Primary Certificates (Other)',
        },
        { label: 'Secondary Certificates', value: 'Secondary Certificates' },
        ...uniqueDocumentTypeOptions.filter(
          (option) => option.value === RESTRICTED_CERTIFICATES_TYPE,
        ),
      ]
    : [
        ...uniqueDocumentTypeOptions,
        { label: 'Owners LOI Wording', value: 'Owners LOI Wording' },
        { label: 'Other documents', value: 'Other' },
      ]

  const chosenType = watch('type')
  const documentNamesOptions = (
    documentsByType.find(
      (documentsOfOneType) => documentsOfOneType.type === chosenType,
    )?.documents || []
  ).map((document) => ({
    label: document.documentName,
    value: document.documentName,
    isDisabled:
      !!document.files &&
      !!document.files.length &&
      !isDateExpired(document?.expires),
  }))

  let documentNameLabel: string
  let fileLabel: string
  switch (chosenType) {
    case 'Certificate':
      documentNameLabel = 'Certificate Name'
      fileLabel = 'Certificate *'
      break
    case 'ISS':
      documentNameLabel = 'ISS Document Name'
      fileLabel = 'ISS Document *'
      break
    case 'Vessel details':
      documentNameLabel = 'Vessel Details Document Name'
      fileLabel = 'Vessel Details Document *'
      break
    default:
      documentNameLabel = 'Document Name'
      fileLabel = 'Document *'
  }

  const isDateRequired = getIsDateRequired(chosenType)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // flexWrap: 'wrap',
        width: '100%',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="caption">
          {isOcrLoading
            ? 'Extracting data from document...'
            : hasFile
              ? 'Fill in the document details below.'
              : 'Upload a document to get started. Data will be auto-filled if detected.'}
        </Typography>
      </Box>

      <DragAndDropFiles
        singleFile
        onChangeHandler={async (files) => {
          setFilesToPost(() =>
            files.map((file) => ({
              key: chosenType || 'pending',
              file,
            })),
          )

          // Run OCR prefill for the first file
          const firstFile = files[0]
          if (!firstFile) return

          const extension = firstFile.name.split('.').pop()?.toLowerCase()
          if (!extension || !ALLOWED_EXTENSIONS.has(extension)) return

          try {
            const ocrResult = await ocrMutation.mutateAsync(firstFile)
            const prefillResult = await extractMutation.mutateAsync({
              text: ocrResult.text,
              documentsByType: documentsByType.map((d) => ({
                type: d.type,
                documentNames: d.documents.map((doc) => doc.documentName),
              })),
            })

            if (prefillResult.documentType) {
              setValue('type', prefillResult.documentType, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            if (prefillResult.documentName) {
              setValue('documentName', prefillResult.documentName, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            if (prefillResult.issueDate) {
              setValue('issued', new Date(prefillResult.issueDate), {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            if (prefillResult.expiryDate) {
              setValue('expires', new Date(prefillResult.expiryDate), {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            if (prefillResult.noExpiryDate) {
              setValue('expires', undefined)
              setNoExpiryDate(true)
            }
            if (prefillResult.noNextSurvey) {
              setValue('nextSurvey', undefined)
              setNoNextSurvey(true)
            }
          } catch {
            setStatus(
              'Document prefill failed. Please fill in the fields manually.',
              8000,
              'error',
            )
          }
        }}
      />

      {hasTooManyFiles && (
        <FormHelperText error role="alert">
          Only one file can be uploaded. Remove the extra files to continue.
        </FormHelperText>
      )}

      {isOcrLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            width: '100%',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}

      {hasFile && !isOcrLoading && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              maxWidth: '799px',
              mb: 4,
              mt: 4,
            }}
          >
            <SelectField
              name="type"
              options={documentTypeOptions}
              placeholder="Document Type"
              onChange={() => setValue('documentName', undefined)}
              required
            />
            {chosenType === 'Other' ||
            chosenType === 'Owners LOI Wording' ||
            chosenType === 'Time Charter' ||
            chosenType === 'Secondary Certificates' ||
            chosenType === 'Primary Certificates (Other)' ? (
              <TextInputField
                name="documentName"
                label={documentNameLabel}
                required
                sx={{ width: 344 }}
                disabled={!chosenType}
              />
            ) : (
              <SelectField
                name="documentName"
                options={documentNamesOptions}
                placeholder={documentNameLabel}
                required
                isDisabled={!chosenType}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {showTechManagerDocuments && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  maxWidth: '799px',
                  mb: 4,
                }}
              >
                <Checkbox
                  disabled={!chosenType}
                  {...register('digitalCertificate')}
                  label="Digital/Electronic Certificate"
                  name="digitalCertificate"
                  checked={isDigitalCertificate}
                  onChange={(e) =>
                    setValue(
                      'digitalCertificate',
                      Boolean((e.target as HTMLInputElement).checked),
                    )
                  }
                />
                <FormControlLabel
                  control={
                    <MUICheckbox
                      disabled={!chosenType}
                      checked={noExpiryDate}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue('expires', undefined)
                        }
                        setNoExpiryDate(e.target.checked)
                      }}
                    />
                  }
                  label="No Expiry Date"
                />
                <FormControlLabel
                  control={
                    <MUICheckbox
                      disabled={!chosenType}
                      checked={noNextSurvey}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue('nextSurvey', undefined)
                        }
                        setNoNextSurvey(e.target.checked)
                      }}
                    />
                  }
                  label="No Next Survey"
                />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                mb: 4,
              }}
            >
              {showTechManagerDocuments && (
                <Autocomplete
                  disabled={!chosenType}
                  limitTags={1}
                  size="small"
                  multiple
                  disableCloseOnSelect
                  options={certificateCategories}
                  value={selectedCategories}
                  onChange={(_, newValue) => {
                    setSelectedCategories(newValue)
                    setValue('certificateCategories', newValue)
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        chosenType === 'Secondary Certificates'
                          ? 'Document Categories'
                          : 'Document Categories (Required)'
                      }
                    />
                  )}
                  sx={{ minWidth: 344 }}
                  slotProps={{ popper: { style: { zIndex: Z_INDEX.Popup } } }}
                />
              )}
              {showTechManagerDocuments && (
                <>
                  <SelectField
                    isDisabled={!chosenType}
                    name="certificateIssuer"
                    options={certificateIssuerOptions}
                    placeholder="Certificate Issuer"
                  />
                </>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                mb: 4,
              }}
            >
              {showTechManagerDocuments && (
                <>
                  <TextInputField
                    sx={{ width: 344 }}
                    name="certificateNumber"
                    label="Certificate Number"
                    disabled={!chosenType}
                  />
                  <TextInputField
                    sx={{ width: 344 }}
                    name="referenceNumber"
                    label="Reference number to physical filing system"
                    disabled={!chosenType}
                  />
                </>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                mb: 4,
              }}
            >
              <DatePickerField
                isDisabled={!chosenType}
                name="issued"
                label="Issue Date"
                required={
                  isDateRequired ||
                  (showTechManagerDocuments &&
                    chosenType === 'Primary Certificates')
                }
              />
              {!noExpiryDate && (
                <DatePickerField
                  isDisabled={!chosenType}
                  name="expires"
                  label="Expiry Date"
                  required={
                    !noExpiryDate &&
                    (isDateRequired ||
                      (showTechManagerDocuments &&
                        chosenType === 'Primary Certificates' &&
                        !noExpiryDate))
                  }
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                mb: 4,
              }}
            >
              {showTechManagerDocuments && (
                <>
                  <DatePickerField
                    name="lastSurvey"
                    label="Last Survey"
                    isDisabled={!chosenType}
                  />
                  {!noNextSurvey && (
                    <DatePickerField
                      name="nextSurvey"
                      label="Next Survey"
                      // required={false}
                      isDisabled={!chosenType}
                    />
                  )}
                </>
              )}
            </Box>
            {showTechManagerDocuments && (
              <Box sx={{ mb: 4 }}>
                <DocumentTagsField
                  disabled={!chosenType}
                  documentTags={tags}
                  existingDocumentTags={existingDocumentTags}
                  onChange={(newTags) => {
                    setTags(newTags)
                    setValue('tags', newTags)
                  }}
                />
              </Box>
            )}
            {showTechManagerDocuments && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <TextInputField
                  multiline
                  minRows={4}
                  maxRows={4}
                  sx={{ width: 714 }}
                  name="remarks"
                  label="Remarks"
                  disabled={!chosenType}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

interface IAddDocumentModal {
  close: () => void
  documentNamesByType: IDocumentsByType[]
  refetchDocuments?: () => void
  existingDocumentTags?: string[]
  showTechManagerDocuments?: boolean
}

export const AddDocumentModal: FC<IAddDocumentModal> = ({
  existingDocumentTags = [],
  showTechManagerDocuments = false,
  refetchDocuments = () => {},
  documentNamesByType = [],
  close,
}) => {
  const { vesselId } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const { setStatus } = useAnimatedAlert()
  const [filesToPost, setFilesToPost] = useState<
    { key: string; file: File }[] | undefined
  >([])
  const [noExpiryDate, setNoExpiryDate] = useState(false)
  const [noNextSurvey, setNoNextSurvey] = useState(false)
  const postDocumentsMutation = usePostDocuments({ vesselId: vesselId! })

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const filePayload = filesToPost?.map(({ file }) => file)

    const formData = new FormData()
    formData.append('type', data.type)
    formData.append('documentName', data.documentName)
    formData.append(
      'digitalCertificate',
      data.digitalCertificate ? 'true' : 'false',
    )
    formData.append('noExpiryDate', noExpiryDate ? 'true' : 'false')
    formData.append('noNextSurvey', noNextSurvey ? 'true' : 'false')
    if (data.issued) {
      formData.append(
        'issued',
        forceDateToZuluTimezone(data.issued).toISOString(),
      )
    }
    if (data.expires) {
      formData.append(
        'expires',
        forceDateToZuluTimezone(data.expires).toISOString(),
      )
    }
    if (data.certificateNumber) {
      formData.append('certificateNumber', data.certificateNumber)
    }
    if (data.referenceNumber) {
      formData.append('referenceNumber', data.referenceNumber)
    }
    if (data.certificateIssuer) {
      formData.append('certificateIssuer', data.certificateIssuer)
    }
    if (data.lastSurvey) {
      formData.append(
        'lastSurvey',
        forceDateToZuluTimezone(data.lastSurvey).toISOString(),
      )
    }
    if (data.nextSurvey) {
      formData.append(
        'nextSurvey',
        forceDateToZuluTimezone(data.nextSurvey).toISOString(),
      )
    }
    if (data.remarks) {
      formData.append('remarks', data.remarks)
    }
    if (data.certificateCategories?.length) {
      data.certificateCategories.forEach((category: string) => {
        formData.append('certificateCategories', category)
      })
    }
    if (data.tags?.length) {
      data.tags.forEach((tag: string) => {
        formData.append('tags', tag)
      })
    }
    filePayload?.forEach((file) => {
      formData.append('file', file)
    })

    setIsLoading(true)

    postDocumentsMutation.mutate(formData, {
      onSuccess: () => {
        setStatus('Document saved', 5000, 'success')
        gtm.pushEvent('document_added', {
          document_name: data.documentName,
          document_category: data.type,
        })
        refetchDocuments()
        close()
      },
      onError: (error) => {
        setStatus(
          invalidInputMessage(error) ?? 'Adding document failed',
          5000,
          'error',
        )
      },
      onSettled: () => {
        setIsLoading(false)
      },
    })
  }

  const methods = useForm<FieldValues>({
    defaultValues: {
      issued: null,
      expires: null,
    },
  })
  const save = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    methods.handleSubmit(onSubmit)()
  }

  const issuedDate = methods.watch('issued')
  const expiryDate = methods.watch('expires')
  const type = methods.watch('type')
  const isDateRequired = getIsDateRequired(type)

  const isFormValid = () => {
    if (showTechManagerDocuments) {
      return (
        !!methods.watch('documentName') &&
        !!methods.watch('type') &&
        (!!methods.watch('issued') ||
          ['Secondary Certificates', 'Primary Certificates (Other)'].includes(
            type,
          )) &&
        (!!methods.watch('expires') ||
          noExpiryDate ||
          ['Secondary Certificates', 'Primary Certificates (Other)'].includes(
            type,
          )) &&
        (!!methods.watch('certificateCategories') ||
          ['Secondary Certificates', 'Primary Certificates (Other)'].includes(
            type,
          )) &&
        filesToPost?.length === 1
      )
    }
    return (
      filesToPost?.length === 1 &&
      (isDateRequired ? !!issuedDate && !!expiryDate : true)
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => save(e)}>
        {vesselId && !!documentNamesByType?.length && (
          <AddDocumentModalContent
            existingDocumentTags={existingDocumentTags}
            setFilesToPost={setFilesToPost}
            filesToPost={filesToPost}
            documentsByType={documentNamesByType}
            showTechManagerDocuments={showTechManagerDocuments}
            noExpiryDate={noExpiryDate}
            setNoExpiryDate={setNoExpiryDate}
            noNextSurvey={noNextSurvey}
            setNoNextSurvey={setNoNextSurvey}
          />
        )}
        {!showTechManagerDocuments && (
          <>
            <br />
            <br />
            <br />
            <br />
          </> // Do not remove this. It exists because date picker dropdowns need to render without portal, which means height: auto does not work.
        )}
        <ModalFooter>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isFormValid()}
            loading={isLoading}
          >
            Save
          </Button>
        </ModalFooter>
      </form>
    </FormProvider>
  )
}
